import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BarChart3 } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify } from "@/components/ui/dialogs";
import { get } from "@/utils/service";
import { apiMessage, shortDate } from "@/utils/api";
import KpiCard from "@/components/platform/KpiCard";
import { countryName, flag } from "@/components/platform/countries";
import { PERIODS } from "./PlatformDashboard";
import AppSelect from "@/components/ui/app-select";

interface OrgAnalytics {
  organization: { id: string; name: string; status: string; country: string | null; city: string | null; created_at: string; suspension_reason: string | null };
  period_days: number;
  members: { total: number; active: number; signed_in_in_period: number; by_role: Array<{ role: string; members: number }> };
  usage: Array<{ date: string; actions: number; sign_ins: number }>;
  records: Array<{ module: string; label: string; total: number; created_in_period: number; actions_in_period: number | null }>;
  last_activity_at: string | null;
  last_sign_in_at: string | null;
}
interface OrgOption { id: string; name: string; country: string | null }

/** Splits a daily series into up to 8 equal buckets for the card graphs. */
const toTrend = (values: number[]) => {
  const size = Math.max(1, Math.ceil(values.length / 8));
  const out: number[] = [];
  for (let i = 0; i < values.length; i += size) out.push(values.slice(i, i + size).reduce((a, b) => a + b, 0));
  return out;
};

/** One organization's usage for system administrators: counts and trends, never its records. */
export default function PlatformAnalytics() {
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<OrgAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const orgId = params.get("org") ?? "";

  useEffect(() => {
    get<{ data: OrgOption[] }>("/platform/organizations", { page_size: 100 })
      .then((r) => {
        setOrgs(r.data);
        if (!params.get("org") && r.data[0]) setParams({ org: r.data[0].id }, { replace: true });
      })
      .catch((e) => notify(apiMessage(e, "Could not load organizations.")));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orgId) return;
    let live = true;
    setLoading(true);
    get<{ data: OrgAnalytics }>(`/platform/analytics/organizations/${orgId}`, { days })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => notify(apiMessage(e, "Could not load this organization's analytics.")))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [orgId, days]);

  const chart = useMemo(() => (data?.usage ?? []).map((u) => ({ ...u, label: new Date(`${u.date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) })), [data]);
  const sum = (k: "actions" | "sign_ins") => (data?.usage ?? []).reduce((n, u) => n + u[k], 0);
  const period = PERIODS.find((p) => p.days === days)?.label.toLowerCase() ?? "";

  return (
    <div className="space-y-5 p-4 sm:p-0">
      <PageHeader title="Analytics" description="How each organization uses the platform: counts only">
        <AppSelect value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs">
          {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
        </AppSelect>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <label className="text-xs text-gray-500">Organization</label>
        <AppSelect value={orgId} onChange={(e) => setParams({ org: e.target.value })} className="min-w-[14rem] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm sm:flex-none">
          {orgs.map((o) => <option key={o.id} value={o.id}>{flag(o.country)} {o.name}</option>)}
        </AppSelect>
        {data && (
          <p className="text-xs text-gray-500">
            {[data.organization.city, data.organization.country ? countryName(data.organization.country) : null].filter(Boolean).join(", ") || "Location not set"} ·
            Created {shortDate(data.organization.created_at)} ·
            <span className={data.organization.status === "active" ? " text-green-700" : " text-red-600"}> {data.organization.status === "active" ? "Active" : `Suspended${data.organization.suspension_reason ? `: ${data.organization.suspension_reason}` : ""}`}</span>
          </p>
        )}
      </div>

      {!orgs.length && !loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center">
          <BarChart3 className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">No organizations yet.</p>
        </div>
      ) : !data || loading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-white" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard dark label="Members" value={data.members.total.toLocaleString()} trend={toTrend(data.usage.map((u) => u.sign_ins))} deltaLabel={`${data.members.active} active memberships`} footer={`Last sign-in: ${data.last_sign_in_at ? shortDate(data.last_sign_in_at) : "never"}`} />
            <KpiCard label="Active users" value={data.members.signed_in_in_period.toLocaleString()} trend={toTrend(data.usage.map((u) => u.sign_ins))} deltaLabel={`signed in, ${period}`} footer={`${data.members.total ? Math.round((data.members.signed_in_in_period / data.members.total) * 100) : 0}% of members`} />
            <KpiCard label="Sign-ins" value={sum("sign_ins").toLocaleString()} trend={toTrend(data.usage.map((u) => u.sign_ins))} deltaLabel={period} footer="Successful sign-ins by members" />
            <KpiCard label="Actions" value={sum("actions").toLocaleString()} trend={toTrend(data.usage.map((u) => u.actions))} deltaLabel={period} footer={`Last activity: ${data.last_activity_at ? shortDate(data.last_activity_at) : "none yet"}`} />
          </div>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">Activity</h2>
            <p className="mb-4 text-xs text-gray-500">Sign-ins and record changes per day, {period}</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 4, bottom: 0, left: -24 }}>
                  <CartesianGrid vertical={false} stroke="#f1f1f1" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={16} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ borderRadius: 12, border: "none", background: "#111827", color: "#fff", fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="actions" name="Actions" stackId="a" fill="#ff6d27" maxBarSize={18} />
                  <Bar dataKey="sign_ins" name="Sign-ins" stackId="a" fill="#111827" radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-3">
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm xl:col-span-2">
              <div className="px-5 pt-5"><h2 className="text-base font-semibold text-gray-900">Records by module</h2><p className="text-xs text-gray-500">How many exist, and how many were created or changed {period}</p></div>
              <div className="mt-3 overflow-x-auto grid-scroll">
                <table className="min-w-full text-xs">
                  <thead><tr className="border-y border-gray-100 bg-gray-50 text-left text-gray-500">
                    <th className="px-5 py-2 font-medium">Module</th><th className="px-4 py-2 font-medium">Total</th><th className="px-4 py-2 font-medium">Created</th><th className="px-4 py-2 font-medium">Changes</th><th className="px-4 py-2 font-medium">Share</th>
                  </tr></thead>
                  <tbody>
                    {data.records.map((r) => {
                      const all = data.records.reduce((n, x) => n + x.total, 0);
                      return (
                        <tr key={r.module} className="border-b border-gray-50">
                          <td className="px-5 py-2.5 font-medium text-gray-900">{r.label}</td>
                          <td className="px-4 py-2.5 text-gray-700">{r.total.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-gray-700">{r.created_in_period.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-gray-700">{r.actions_in_period === null ? "—" : r.actions_in_period.toLocaleString()}</td>
                          <td className="px-4 py-2.5"><div className="h-1.5 w-24 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${all ? (r.total / all) * 100 : 0}%` }} /></div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="rounded-2xl bg-primary/10 p-5">
              <h2 className="text-base font-semibold text-gray-900">Members by role</h2>
              <div className="mt-4 space-y-3">
                {data.members.by_role.map((r) => (
                  <div key={r.role}>
                    <div className="flex justify-between text-xs"><span className="font-medium text-gray-800">{r.role}</span><span className="text-gray-500">{r.members}</span></div>
                    <div className="mt-1 h-1.5 rounded-full bg-white"><div className="h-1.5 rounded-full bg-gray-900" style={{ width: `${data.members.total ? (r.members / data.members.total) * 100 : 0}%` }} /></div>
                  </div>
                ))}
                {!data.members.by_role.length && <p className="text-xs text-gray-500">No members yet.</p>}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
